import { useState, useEffect, useId, useRef } from 'react';
import { motion, useReducedMotion } from 'framer-motion';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { X, ExternalLink, Clock, Globe, User, Mail, Phone, Hash, Calendar, Tag, TrendingUp, Wifi, GitMerge, Layers, Trash2, ShoppingCart, DollarSign, CheckCircle2, XCircle } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Badge } from '@/components/ui/badge';
import { CopyButton, QueryError, money } from '@/workspace/ui';
import { Separator } from '@/components/ui/separator';
import { Skeleton } from '@/components/ui/skeleton';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

/* How each identity merge happened — mirrors backend STITCH_CONFIDENCE methods */
const MERGE_METHOD_LABELS = {
  email_exact:     'email match',
  email_ci:        'email match',
  uid_cookie:      'server cookie',
  cookie_silo:     'browser storage',
  url_adoption:    'decorated link',
  frame_handshake: 'iframe handshake',
  session:         'shared session',
  client_stitch:   'tracker stitch',
  ip_cross_match:  'IP + device match',
  ip_companion:    'IP + device match',
};

import { useTimezone } from '@/components/TimezoneContext';

function formatDateTime(dt) {
  if (!dt) return '—';
  const d = new Date(dt);
  return d.toLocaleString('en-US', {
    month: 'short', day: 'numeric', year: 'numeric',
    hour: '2-digit', minute: '2-digit', second: '2-digit'
  });
}

function parseUrlParams(url) {
  try {
    const u = new URL(url);
    const params = [];
    u.searchParams.forEach((val, key) => params.push({ key, val }));
    return { base: u.origin + u.pathname, params };
  } catch {
    return { base: url, params: [] };
  }
}


const InfoRow = ({ icon: Icon, label, value, mono, copyable, accent }) => (
  <div
    className="sp-contact-info-row grid items-center border-b last:border-0"
    style={{ gridTemplateColumns: '148px 1fr auto', borderColor: 'var(--stroke)' }}
  >
    {/* Label */}
    <div className="flex items-center gap-2 px-4 py-2.5 shrink-0" style={{ backgroundColor: 'var(--surface-2)' }}>
      <Icon size={13} style={{ color: accent || 'var(--text-dim)', flexShrink: 0 }} />
      <span className="text-[11px] font-medium" style={{ color: 'var(--text-dim)' }}>{label}</span>
    </div>
    {/* Value */}
    <div
      className={`px-4 py-2.5 overflow-hidden ${mono ? 'break-all' : 'truncate'}`}
      style={{
        color:       value ? 'var(--ink)' : 'var(--text-dim)',
        fontFamily:  mono ? 'var(--font-mono)' : 'var(--font)',
        fontSize:    mono ? '12px' : '13px',
        fontWeight:  mono ? 400 : 500,
      }}
      title={value || undefined}
    >
      {value || 'Not provided'}
    </div>
    {/* Copy */}
    <div className="px-3 py-3">
      {copyable && value && <CopyButton text={value} label={`Copy ${label}`} />}
    </div>
  </div>
);

const AttrRow = ({ label, value, hint }) => {
  if (!value) return null;
  return (
    <div className="flex items-start gap-3 py-2 px-4 min-w-0">
      <div className="text-[11px] font-medium w-36 shrink-0 pt-0.5" style={{ color: 'var(--text-dim)' }}>{label}</div>
      <div
        className="flex-1 min-w-0"
        style={{
          fontSize: '12px',
          color: 'var(--brand-red)',
          fontFamily: 'var(--font-mono)',
          wordBreak: 'break-all',
          overflowWrap: 'anywhere',
        }}
      >
        {value}
        {hint && <div className="text-[11px] mt-0.5" style={{ color: 'var(--text-muted)', fontFamily: 'inherit' }}>{hint}</div>}
      </div>
      <CopyButton text={value} label={`Copy ${label}`} />
    </div>
  );
};

// Meta campaign / ad set / ad ids with their names when the workspace has a Meta Ads connection.
const MetaIdRows = ({ attribution }) => {
  const ids = ['campaign_id', 'adset_id', 'ad_id'].map(k => attribution?.[k]).filter(Boolean);
  const { data: names } = useQuery({
    queryKey: ['meta-names', ids.join(',')],
    queryFn: () => fetch(`${BACKEND_URL}/api/integrations/meta/names?ids=${encodeURIComponent(ids.join(','))}`).then(r => (r.ok ? r.json() : {})),
    enabled: ids.length > 0, staleTime: 300_000,
  });
  const hint = id => (names?.[id] ? `${names[id].name}${names[id].status ? ` · ${names[id].status.toLowerCase()}` : ''}` : undefined);
  return (<>
    <AttrRow label="campaign_id" value={attribution?.campaign_id} hint={hint(attribution?.campaign_id)} />
    <AttrRow label="adset_id" value={attribution?.adset_id} hint={hint(attribution?.adset_id)} />
    <AttrRow label="ad_id" value={attribution?.ad_id} hint={hint(attribution?.ad_id)} />
  </>);
};

const UrlVisitItem = ({ visit, index }) => {
  const { base, params } = parseUrlParams(visit.current_url || '');
  const { base: refBase, params: refParams } = parseUrlParams(visit.referrer_url || '');

  return (
    <div data-testid="contact-url-row" className="relative pl-6 pb-5 min-w-0">
      {/* Timeline dot */}
      <div
        className="absolute left-0 top-2 w-3.5 h-3.5 rounded-full border-2 flex items-center justify-center"
        style={{
          backgroundColor: '#ffffff',
          borderColor: index === 0 ? 'var(--lime)' : 'var(--stroke)',
        }}
      >
        <div className="w-1.5 h-1.5 rounded-full"
          style={{ backgroundColor: index === 0 ? 'var(--lime)' : 'var(--text-dim)' }}
        />
      </div>

      <div className="rounded-lg p-3 border min-w-0 overflow-hidden" style={{ backgroundColor: '#ffffff', borderColor: 'var(--stroke)' }}>
        {/* Timestamp + title */}
        <div className="flex items-center justify-between gap-2 mb-2 min-w-0">
          <div className="flex items-center gap-1.5 shrink-0">
            <Clock size={11} style={{ color: 'var(--text-dim)' }} />
            <span className="text-xs" style={{ color: 'var(--text-dim)' }}>{formatDateTime(visit.timestamp)}</span>
          </div>
          {visit.page_title && (
            <Badge variant="secondary" className="text-xs px-2 py-0 max-w-[160px] truncate shrink-0"
              style={{ backgroundColor: 'var(--lime-tint)', color: 'var(--brand-red)', border: '1px solid var(--lime-border)' }}
            >
              {visit.page_title}
            </Badge>
          )}
        </div>

        {/* Current URL */}
        <div className="mb-2 min-w-0">
          <div className="flex items-center gap-1.5 mb-1">
            <Globe size={11} className="shrink-0" style={{ color: 'var(--brand-red)' }} />
            <span className="text-xs font-medium shrink-0" style={{ color: 'var(--text-muted)' }}>Current URL</span>
            <CopyButton text={visit.current_url} label="Copy URL" />
          </div>
          <div
            className="text-xs font-mono"
            style={{
              color: 'var(--text)',
              fontFamily: 'var(--font-mono)',
              wordBreak: 'break-all',
              overflowWrap: 'anywhere',
            }}
          >
            {base}
          </div>
          {params.length > 0 && (
            <div className="flex flex-wrap gap-1 mt-1.5">
              {params.map(({ key, val }) => (
                <Badge key={key} variant="secondary" className="text-xs font-mono px-1.5 py-0 max-w-full"
                  style={{
                    backgroundColor: 'rgba(245,158,11,0.1)',
                    color: 'var(--amber-warn)',
                    border: '1px solid rgba(245,158,11,0.2)',
                    fontFamily: 'var(--font-mono)',
                    wordBreak: 'break-all',
                    overflowWrap: 'anywhere',
                    whiteSpace: 'normal',
                  }}
                >
                  {key}={val}
                </Badge>
              ))}
            </div>
          )}
        </div>

        {/* Referrer */}
        {visit.referrer_url && (
          <div className="min-w-0">
            <Separator className="my-2" style={{ backgroundColor: 'var(--stroke)' }} />
            <div className="flex items-center gap-1.5 mb-1">
              <ExternalLink size={11} className="shrink-0" style={{ color: 'var(--text-dim)' }} />
              <span className="text-xs shrink-0" style={{ color: 'var(--text-dim)' }}>Referrer</span>
            </div>
            <div
              className="text-xs font-mono"
              style={{
                color: 'var(--text-muted)',
                fontFamily: 'var(--font-mono)',
                wordBreak: 'break-all',
                overflowWrap: 'anywhere',
              }}
            >
              {refBase}
            </div>
            {refParams.length > 0 && (
              <div className="flex flex-wrap gap-1 mt-1.5">
                {refParams.map(({ key, val }) => (
                  <Badge key={key} variant="secondary" className="text-xs font-mono px-1.5 py-0 max-w-full"
                    style={{
                      backgroundColor: 'rgba(255,255,255,0.05)',
                      color: 'var(--text-muted)',
                      border: '1px solid var(--stroke)',
                      fontFamily: 'var(--font-mono)',
                      wordBreak: 'break-all',
                      overflowWrap: 'anywhere',
                      whiteSpace: 'normal',
                    }}
                  >
                    {key}={val}
                  </Badge>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Visit-level attribution */}
        {visit.attribution && Object.values(visit.attribution).some(v => v && typeof v !== 'object') && (
          <div className="mt-2 min-w-0">
            <Separator className="my-2" style={{ backgroundColor: 'var(--stroke)' }} />
            <div className="flex items-center gap-1.5 mb-1.5">
              <Tag size={11} className="shrink-0" style={{ color: 'var(--mint-success)' }} />
              <span className="text-xs" style={{ color: 'var(--text-dim)' }}>Attribution (this visit)</span>
            </div>
            <div className="flex flex-wrap gap-1">
              {Object.entries(visit.attribution)
                .filter(([k, v]) => v && k !== 'extra' && typeof v === 'string')
                .map(([k, v]) => (
                  <Badge key={k} variant="secondary" className="text-xs font-mono px-1.5 py-0 max-w-full"
                    style={{
                      backgroundColor: 'rgba(69,209,156,0.08)',
                      color: 'var(--mint-success)',
                      border: '1px solid rgba(69,209,156,0.15)',
                      fontFamily: 'var(--font-mono)',
                      wordBreak: 'break-all',
                      overflowWrap: 'anywhere',
                      whiteSpace: 'normal',
                    }}
                  >
                    {k}={v}
                  </Badge>
                ))
              }
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

// ── ModalInner — keyed per contact ───────────────────────────────────────────
// Owns all state, fetch, and UI. The key={contactId} on this component means it
// remounts on every contact switch. The outer Dialog shell (and its overlay)
// stays mounted, so the overlay never flashes.
const ModalInner = ({ contactId, defaultTab, onClose, onDelete }) => {
  const tabId = useId();
  const reducedMotion = useReducedMotion();
  const [activeTab, setActiveTab] = useState(defaultTab || 'overview');
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const deletePending = useRef(false);
  const [deleteError, setDeleteError] = useState('');
  const deleteContact = async event => {
    event.preventDefault();
    if (deletePending.current || !onDelete) return;
    deletePending.current = true;
    setDeleting(true); setDeleteError('');
    try {
      const deleted = await onDelete(contactId);
      if (deleted !== true) throw new Error('Could not delete this contact. Please try again.');
      setDeleteOpen(false);
      onClose();
    } catch (error) {
      setDeleteError(error.message || 'Could not delete this contact. Please try again.');
    } finally { deletePending.current = false; setDeleting(false); }
  };
  const { formatDateTime: tzFmt } = useTimezone();
  // Override the module-level helper with the timezone-aware one
  const fmt = (dt) => tzFmt(dt) || formatDateTime(dt);

  useEffect(() => { setActiveTab(defaultTab || 'overview'); }, [defaultTab]);

  const { data: contact, isLoading, error, refetch } = useQuery({
    queryKey: ['contact', contactId],
    queryFn:  () => fetch(`${BACKEND_URL}/api/contacts/${contactId}`)
      .then(r => { if (!r.ok) throw new Error('Failed'); return r.json(); }),
    enabled: !!contactId,
  });

  // Manual tagging — suggestions are every tag the workspace already knows
  const qc = useQueryClient();
  const [newTag, setNewTag]   = useState('');
  const [tagBusy, setTagBusy] = useState(false);
  const tagPending = useRef(false);
  const [tagError, setTagError] = useState('');
  const { data: tagData } = useQuery({
    queryKey: ['tags'],
    queryFn: () => fetch(`${BACKEND_URL}/api/tags`).then(r => (r.ok ? r.json() : { tags: [] })),
    staleTime: 30_000,
  });
  const suggestedTags = (tagData?.tags || []).filter(t => !(contact?.tags || []).includes(t));
  const tagRequest = async (url, method, body) => {
    if (tagPending.current) return;
    tagPending.current = true;
    setTagBusy(true); setTagError('');
    try {
      const res = await fetch(url, {
        method, headers: { 'Content-Type': 'application/json' },
        body: body ? JSON.stringify(body) : undefined,
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.detail || 'Tag update failed');
      setNewTag('');
      qc.invalidateQueries({ queryKey: ['contact', contactId] });
      qc.invalidateQueries({ queryKey: ['contacts'] });
      qc.invalidateQueries({ queryKey: ['tags'] });
    } catch (e) {
      setTagError(e.message || 'Tag update failed');
      toast.error(e.message);
    } finally {
      tagPending.current = false;
      setTagBusy(false);
    }
  };
  const addTag = () => {
    if (newTag.trim()) tagRequest(`${BACKEND_URL}/api/contacts/${contactId}/tags`, 'POST', { tag: newTag.trim() });
  };
  const removeTag = (t) =>
    tagRequest(`${BACKEND_URL}/api/contacts/${contactId}/tags/${encodeURIComponent(t)}`, 'DELETE');

  const hasAttribution = contact?.attribution && (
    Object.entries(contact.attribution).some(([k, v]) => {
      if (k === 'extra') return v && typeof v === 'object' && Object.keys(v).length > 0;
      return v && typeof v !== 'object';
    })
  );

  return (
    <>
        <DialogHeader className="sp-contact-inspector-head px-6 pt-5 pb-0 shrink-0" style={{ backgroundColor: 'var(--surface-2)', borderBottom: '1px solid var(--stroke)', paddingBottom: '16px' }}>
          <DialogDescription className="sr-only">Contact profile, attribution, visit history and sales.</DialogDescription>
          <div className="flex items-start justify-between gap-4">
            <div className="min-w-0 flex-1">
              <DialogTitle
                className="text-[15px] font-semibold mb-0.5"
                style={{ color: 'var(--ink)' }}
              >
                {isLoading ? (
                  <Skeleton className="h-6 w-44" style={{ backgroundColor: 'rgba(32,33,31,0.12)' }} />
                ) : (
                  error ? 'Contact unavailable' : contact?.name || contact?.email || 'Anonymous Contact'
                )}
              </DialogTitle>
              {isLoading ? (
                <Skeleton className="h-3 w-72 mt-1.5" style={{ backgroundColor: 'rgba(32,33,31,0.08)' }} />
              ) : contact ? (
                <p className="text-xs font-mono" style={{ color: 'var(--text-dim)', fontFamily: 'var(--font-mono)' }}>
                  ID: {contact.contact_id}
                </p>
              ) : null}
            </div>
            <div className="flex items-center gap-1.5 shrink-0">
              {contact && onDelete && (
                <AlertDialog open={deleteOpen} onOpenChange={open => { if (!deleting) { setDeleteOpen(open); setDeleteError(''); } }}>
                  <AlertDialogTrigger asChild>
                    <button
                      data-testid="contact-detail-delete-button"
                      className="rounded-md p-1 hover:bg-red-500/10 transition-colors duration-150"
                      aria-label="Delete contact"
                      title="Delete contact"
                      style={{ color: 'var(--red-error, #ef4444)' }}
                    >
                      <Trash2 size={15} />
                    </button>
                  </AlertDialogTrigger>
                  <AlertDialogContent
                    style={{ backgroundColor: '#ffffff', borderColor: 'var(--stroke)', color: 'var(--text)' }}
                  >
                    <AlertDialogHeader>
                      <AlertDialogTitle style={{ color: 'var(--text)' }}>Delete Contact</AlertDialogTitle>
                      <AlertDialogDescription style={{ color: 'var(--text-muted)' }}>
                        Are you sure you want to delete <strong style={{ color: 'var(--text)' }}>{contact.name || contact.email || 'this contact'}</strong>?
                        This will permanently remove the contact and all their visit history.
                      </AlertDialogDescription>
                    </AlertDialogHeader>
                    {deleteError && <p className="sp-directory-error" role="alert">{deleteError}</p>}
                    <AlertDialogFooter>
                      <AlertDialogCancel disabled={deleting} style={{ backgroundColor: '#ffffff', borderColor: 'var(--stroke)', color: 'var(--text)' }}>
                        Cancel
                      </AlertDialogCancel>
                      <AlertDialogAction
                        data-testid="contact-detail-confirm-delete-button"
                        onClick={deleteContact} disabled={deleting}
                        style={{ backgroundColor: '#ef4444', color: '#fff', border: 'none' }}
                      >
                        {deleting ? 'Deleting…' : 'Delete contact'}
                      </AlertDialogAction>
                    </AlertDialogFooter>
                  </AlertDialogContent>
                </AlertDialog>
              )}
              <button
                data-testid="contact-detail-modal-close-button"
                onClick={onClose} disabled={deleting}
                className="shrink-0 rounded-md p-1 hover:bg-white/5 transition-colors duration-150"
                aria-label="Close"
                style={{ color: 'var(--text-dim)' }}
              >
                <X size={16} />
              </button>
            </div>
          </div>
        </DialogHeader>

        {/* flex-1 min-h-0 = fills remaining height without ever growing the modal */}
        <div className="sp-contact-inspector-body flex-1 min-h-0 flex flex-col px-6 pb-6 mt-4 overflow-hidden">
          {error ? (
            <QueryError message="Could not load contact. Your data has not been changed." onRetry={refetch} />
          ) : (() => {
            const tabs = [
              { id: 'overview',    label: 'Overview' },
              { id: 'attribution', label: 'Attribution', badge: hasAttribution ? '●' : null, badgeGreen: true },
              { id: 'urls',        label: 'URL History', badge: contact?.visits?.length || null },
              { id: 'sales',       label: 'Sales',       badge: contact?.sales?.length  || null, badgeRed: true },
            ];

            const tabContent = () => {
              if (isLoading) return (
                <div className="space-y-3 pt-2">
                  {[...Array(5)].map((_, i) => <Skeleton key={i} className="h-12 w-full rounded-lg" style={{ backgroundColor: 'var(--stroke)' }} />)}
                </div>
              );
              if (!contact) return null;

              if (activeTab === 'overview') return (
                <div className="space-y-4">
                  {/* Compact grid info table */}
                  <div className="rounded-lg border overflow-hidden" style={{ borderColor: 'var(--stroke)', backgroundColor: '#ffffff' }}>
                    <InfoRow icon={User}     label="Full Name"    value={contact.name}  />
                    <InfoRow icon={Mail}     label="Email"        value={contact.email} copyable />
                    <InfoRow icon={Phone}    label="Phone"        value={contact.phone} />
                    <InfoRow icon={Hash}     label="Contact ID"   value={contact.contact_id} mono copyable />
                    <InfoRow icon={Wifi}     label="IP Address"   value={contact.client_ip} mono />
                    <InfoRow icon={Globe}    label="User Agent"   value={contact.user_agent} mono copyable />
                    <InfoRow icon={Layers}   label="Session ID"   value={contact.session_id} mono copyable />
                    <InfoRow icon={Calendar} label="First Seen"   value={fmt(contact.created_at)} />
                    <InfoRow icon={Calendar} label="Last Updated" value={fmt(contact.updated_at)} />
                    <div className="grid items-start border-t" style={{ gridTemplateColumns: '148px 1fr auto', borderColor: 'var(--stroke)' }}>
                      <div className="flex items-center gap-2 px-4 py-3" style={{ backgroundColor: 'var(--surface-2)' }}>
                        <Tag size={13} style={{ color: 'var(--text-dim)' }} />
                        <span className="text-[11px] font-semibold" style={{ color: 'var(--text-dim)' }}>Tags</span>
                      </div>
                      <div className="px-4 py-2.5 flex flex-wrap items-center gap-1.5">
                        {(contact.tags || []).map(tag => (
                          <span key={tag} className="inline-flex items-center gap-1 text-[11px] px-1.5 rounded"
                            style={{ height: 20, fontWeight: 500, backgroundColor: 'var(--surface-2)', color: 'var(--text-muted)', border: '1px solid var(--stroke)' }}>
                            {tag}
                            <button onClick={() => removeTag(tag)} disabled={tagBusy} aria-label={`Remove tag ${tag}`}
                              data-testid={`contact-tag-remove-${tag}`} style={{ color: 'var(--text-dim)' }}>
                              <X size={10} />
                            </button>
                          </span>
                        ))}
                        <input
                          list="contact-tag-suggestions"
                          aria-label="Add contact tag" disabled={tagBusy}
                          value={newTag}
                          onChange={e => setNewTag(e.target.value)}
                          onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); addTag(); } }}
                          placeholder="add tag…"
                          data-testid="contact-tag-input"
                          className="text-[12px] px-2 rounded-md outline-none"
                          style={{ height: 24, width: 140, backgroundColor: '#ffffff', border: '1px solid var(--stroke)', color: 'var(--ink)' }}
                        />
                        <datalist id="contact-tag-suggestions">
                          {suggestedTags.map(t => <option key={t} value={t} />)}
                        </datalist>
                        <button type="button" onClick={addTag} disabled={tagBusy || !newTag.trim()} data-testid="contact-tag-add"
                          className="text-[11px] font-semibold px-2 rounded-md"
                          style={{ height: 24, backgroundColor: 'var(--ink)', color: '#fff', opacity: newTag.trim() ? 1 : 0.5 }}>
                          {tagBusy ? 'Saving…' : 'Add tag'}
                        </button>
                        {tagError && <span className="sp-directory-error" role="alert">{tagError}</span>}
                      </div>
                      <div />
                    </div>
                    {(contact.merge_log?.length > 0 || contact.merged_children?.length > 0) && (
                      <div className="grid items-start border-t" style={{ gridTemplateColumns: '148px 1fr auto', borderColor: 'var(--stroke)' }}>
                        <div className="flex items-center gap-2 px-4 py-3" style={{ backgroundColor: 'var(--surface-2)' }}>
                          <GitMerge size={13} style={{ color: 'var(--mint-success)' }} />
                          <span className="text-[11px] font-semibold" style={{ color: 'var(--text-dim)' }}>Stitched</span>
                        </div>
                        <div className="px-4 py-3 flex flex-col gap-1.5">
                          {typeof contact.identity_confidence === 'number' && contact.identity_confidence < 1 && (
                            <span className="text-[11px]" style={{ color: 'var(--text-muted)' }}>
                              Identity confidence {Math.round(contact.identity_confidence * 100)}% — the weakest merge below
                            </span>
                          )}
                          {(contact.merge_log?.length > 0
                            ? contact.merge_log.slice().reverse()
                            : (contact.merged_children || []).map(cid => ({ child: cid }))
                          ).map((edge, i) => (
                            <div key={`${edge.child}-${i}`} className="flex items-center gap-2 flex-wrap">
                              <span className="text-xs font-mono break-all" style={{ color: 'var(--mint-success)', fontFamily: 'var(--font-mono)' }}>{edge.child}</span>
                              {edge.method && (
                                <span className="inline-flex items-center text-[11px] px-1.5 rounded shrink-0"
                                  style={{ height: 18, backgroundColor: 'var(--surface-2)', color: 'var(--text-muted)', border: '1px solid var(--stroke)' }}>
                                  {MERGE_METHOD_LABELS[edge.method] || edge.method}
                                  {typeof edge.confidence === 'number' ? ` · ${Math.round(edge.confidence * 100)}%` : ''}
                                </span>
                              )}
                            </div>
                          ))}
                        </div>
                        <div />
                      </div>
                    )}
                  </div>

                  {/* Sale flashcard — green highlight with $ icon */}
                  {contact.sales?.length > 0 && (
                    <div className="space-y-2">
                      {contact.sales.map(sale => {
                        const fmtAmt = (sale.amount_signed ?? sale.amount) != null
                          ? money(sale.amount_signed ?? sale.amount, sale.currency)
                          : null;
                        return (
                          <div key={sale.id} className="relative rounded-lg overflow-hidden border px-5 py-4"
                            style={{ backgroundColor: 'var(--lime-tint)', borderColor: 'var(--lime-border)' }}>
                            <div className="absolute top-3.5 right-3.5 w-8 h-8 rounded-md flex items-center justify-center"
                              style={{ backgroundColor: '#ffffff', border: '1px solid var(--stroke)' }}>
                              <DollarSign size={14} style={{ color: 'var(--ink)' }} />
                            </div>
                            {fmtAmt && (
                              <div className="text-lg font-semibold tabular-nums mb-0.5 pr-12" style={{ color: 'var(--ink)' }}>
                                {fmtAmt}
                              </div>
                            )}
                            <div className="text-[13px] pr-12" style={{ color: 'var(--text-muted)' }}>{sale.product || 'Purchase'}</div>
                            <div className="flex items-center gap-2 mt-1.5 flex-wrap">
                              <span className="inline-flex items-center gap-1 text-[11px] px-1.5 rounded"
                                style={{ height: 20, fontWeight: 600, backgroundColor: 'var(--lime-soft)', color: 'var(--ink)', border: '1px solid var(--lime-soft)' }}>
                                <CheckCircle2 size={10} />{sale.status || 'unknown'}
                              </span>
                              {sale.source && <span className="text-[11px]" style={{ color: 'var(--text-muted)' }}>via {sale.source}</span>}
                              <span className="text-[11px]" style={{ color: 'var(--text-dim)' }}>
                                {new Date(sale.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                              </span>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              );

              if (activeTab === 'attribution') return (
                <div className="space-y-4">
                  {hasAttribution ? (<>
                    {(contact.attribution?.utm_source || contact.attribution?.utm_medium || contact.attribution?.utm_campaign || contact.attribution?.utm_term || contact.attribution?.utm_content || contact.attribution?.utm_id) && (
                      <div>
                        <p className="text-[11px] font-semibold mb-2 px-1" style={{ color: 'var(--text-dim)' }}>UTM Parameters</p>
                        <div className="rounded-lg border divide-y" style={{ borderColor: 'var(--stroke)', backgroundColor: '#ffffff' }}>
                          <AttrRow label="utm_source" value={contact.attribution?.utm_source} />
                          <AttrRow label="utm_medium" value={contact.attribution?.utm_medium} />
                          <AttrRow label="utm_campaign" value={contact.attribution?.utm_campaign} />
                          <AttrRow label="utm_term" value={contact.attribution?.utm_term} />
                          <AttrRow label="utm_content" value={contact.attribution?.utm_content} />
                          <AttrRow label="utm_id" value={contact.attribution?.utm_id} />
                        </div>
                      </div>
                    )}
                    {(contact.attribution?.campaign_id || contact.attribution?.adset_id || contact.attribution?.ad_id || contact.attribution?.fb_ad_set_id || contact.attribution?.google_campaign_id) && (
                      <div>
                        <p className="text-[11px] font-semibold mb-2 px-1" style={{ color: 'var(--text-dim)' }}>Ad Platform IDs</p>
                        <div className="rounded-lg border divide-y" style={{ borderColor: 'var(--stroke)', backgroundColor: '#ffffff' }}>
                          <MetaIdRows attribution={contact.attribution} />
                          <AttrRow label="fb_ad_set_id" value={contact.attribution?.fb_ad_set_id} />
                          <AttrRow label="google_campaign_id" value={contact.attribution?.google_campaign_id} />
                        </div>
                      </div>
                    )}
                    {['fbclid', 'fbc', 'fbp', 'gclid', 'wbraid', 'gbraid', 'ttclid', 'msclkid', 'twclid', 'li_fat_id', 'epik', 'sccid', 'source_link_tag'].some(k => contact.attribution?.[k]) && (
                      <div>
                        <p className="text-[11px] font-semibold mb-2 px-1" style={{ color: 'var(--text-dim)' }}>Click IDs & FB Cookies</p>
                        <div className="rounded-lg border divide-y" style={{ borderColor: 'var(--stroke)', backgroundColor: '#ffffff' }}>
                          <AttrRow label="fbclid" value={contact.attribution?.fbclid} />
                          <AttrRow label="fbc (FB Click)" value={contact.attribution?.fbc} />
                          <AttrRow label="fbp (FB Browser)" value={contact.attribution?.fbp} />
                          <AttrRow label="gclid" value={contact.attribution?.gclid} />
                          <AttrRow label="wbraid (Google)" value={contact.attribution?.wbraid} />
                          <AttrRow label="gbraid (Google)" value={contact.attribution?.gbraid} />
                          <AttrRow label="ttclid (TikTok)" value={contact.attribution?.ttclid} />
                          <AttrRow label="msclkid (Bing)" value={contact.attribution?.msclkid} />
                          <AttrRow label="twclid (X/Twitter)" value={contact.attribution?.twclid} />
                          <AttrRow label="li_fat_id (LinkedIn)" value={contact.attribution?.li_fat_id} />
                          <AttrRow label="epik (Pinterest)" value={contact.attribution?.epik} />
                          <AttrRow label="sccid (Snapchat)" value={contact.attribution?.sccid} />
                          <AttrRow label="source_link_tag" value={contact.attribution?.source_link_tag} />
                        </div>
                      </div>
                    )}
                    {contact.attribution?.extra && Object.keys(contact.attribution.extra).length > 0 && (
                      <div>
                        <p className="text-[11px] font-semibold mb-2 px-1" style={{ color: 'var(--text-dim)' }}>Other Parameters</p>
                        <div className="rounded-lg border divide-y" style={{ borderColor: 'var(--stroke)', backgroundColor: '#ffffff' }}>
                          {Object.entries(contact.attribution.extra).map(([k, v]) => <AttrRow key={k} label={k} value={v} />)}
                        </div>
                      </div>
                    )}
                  </>) : (
                    <div className="flex flex-col items-center justify-center py-12 gap-2" style={{ color: 'var(--text-dim)' }}>
                      <TrendingUp size={32} /><p className="text-sm">No attribution data for this contact.</p>
                    </div>
                  )}
                </div>
              );

              if (activeTab === 'urls') return contact.visits?.length > 0 ? (
                <ScrollArea className="h-[400px]">
                  <div className="url-timeline-rail pr-4">
                    {contact.visits.map((visit, i) => <UrlVisitItem key={visit.id} visit={visit} index={i} />)}
                  </div>
                </ScrollArea>
              ) : (
                <div className="flex flex-col items-center justify-center py-12 gap-2" style={{ color: 'var(--text-dim)' }}>
                  <Globe size={32} /><p className="text-sm">No URL visits recorded yet.</p>
                </div>
              );

              if (activeTab === 'sales') return contact.sales?.length > 0 ? (
                <div className="space-y-3">
                  {contact.sales.map(sale => {
                    const kind = ({ completed: 'good', paid: 'good', success: 'good', refunded: 'bad', failed: 'bad', cancelled: 'bad' })[sale.status?.toLowerCase()] || 'neutral';
                    const SI = kind === 'good' ? CheckCircle2 : kind === 'bad' ? XCircle : Clock;
                    const pill = kind === 'good'
                      ? { backgroundColor: 'var(--lime-soft)', color: 'var(--ink)', border: '1px solid var(--lime-soft)', fontWeight: 600 }
                      : kind === 'bad'
                        ? { backgroundColor: 'var(--surface-2)', color: 'var(--red-error)', border: '1px solid var(--stroke)', fontWeight: 500 }
                        : { backgroundColor: 'var(--surface-2)', color: 'var(--text-muted)', border: '1px solid var(--stroke)', fontWeight: 500 };
                    return (
                      <div key={sale.id} className="rounded-lg border overflow-hidden"
                        style={kind === 'good'
                          ? { backgroundColor: 'var(--lime-tint)', borderColor: 'var(--lime-border)' }
                          : { backgroundColor: '#ffffff', borderColor: 'var(--stroke)' }}>
                        <div className="flex items-start justify-between gap-4 px-5 py-4">
                          <div className="flex items-start gap-3">
                            <div className="w-8 h-8 rounded-md flex items-center justify-center shrink-0 mt-0.5"
                              style={{ backgroundColor: '#ffffff', border: '1px solid var(--stroke)' }}>
                              <ShoppingCart size={14} style={{ color: 'var(--ink)' }} />
                            </div>
                            <div>
                              <p className="text-[13px] font-medium" style={{ color: 'var(--ink)' }}>{sale.product || 'Purchase'}</p>
                              {sale.source && <p className="text-[11px] mt-0.5" style={{ color: 'var(--text-dim)' }}>via {sale.source}</p>}
                            </div>
                          </div>
                          <div className="text-right shrink-0">
                            <p className="text-[16px] font-semibold tabular-nums" style={{ color: 'var(--ink)' }}>
                              {(sale.amount_signed ?? sale.amount) != null ? money(sale.amount_signed ?? sale.amount, sale.currency) : '—'}
                            </p>
                            <span className="inline-flex items-center gap-1 text-[11px] px-1.5 rounded mt-1" style={{ height: 20, ...pill }}>
                              <SI size={10} />{sale.status || 'unknown'}
                            </span>
                          </div>
                        </div>
                        <div className="px-5 pb-3">
                          <p className="text-[11px]" style={{ color: 'var(--text-dim)' }}>
                            {new Date(sale.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
                          </p>
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className="flex flex-col items-center justify-center py-12 gap-2" style={{ color: 'var(--text-dim)' }}>
                  <ShoppingCart size={32} /><p className="text-sm">No purchases recorded yet.</p>
                </div>
              );

              return null;
            };

            return (
              <>
                {/* Profile sections stay visible while the inspector body scrolls. */}
                <div className="sp-contact-tabs" role="tablist" aria-label="Profile sections"
                  >
                  {tabs.map(tab => (
                    <button
                      key={tab.id}
                      type="button" role="tab" id={`${tabId}-${tab.id}-tab`} aria-controls={`${tabId}-panel`}
                      data-testid={`contact-${tab.id}-tab`} aria-selected={activeTab === tab.id} tabIndex={activeTab === tab.id ? 0 : -1}
                      onClick={() => setActiveTab(tab.id)}
                      onKeyDown={event => {
                        const index = tabs.findIndex(item => item.id === tab.id);
                        const next = event.key === 'ArrowRight' ? (index + 1) % tabs.length : event.key === 'ArrowLeft' ? (index - 1 + tabs.length) % tabs.length : event.key === 'Home' ? 0 : event.key === 'End' ? tabs.length - 1 : null;
                        if (next === null) return;
                        event.preventDefault(); setActiveTab(tabs[next].id);
                        event.currentTarget.parentElement.querySelectorAll('[role="tab"]')[next].focus();
                      }}
                      className="relative px-3 text-[13px] font-medium rounded-md z-10 flex items-center gap-1.5 transition-colors duration-100"
                      style={{
                        height: 42,
                        backgroundColor: 'transparent',
                        color: activeTab === tab.id ? 'var(--ink)' : 'var(--text-muted)',
                        fontWeight: activeTab === tab.id ? 600 : 500,
                        borderBottom: activeTab === tab.id ? '2px solid #3867ef' : '2px solid transparent',
                      }}
                    >
                      <span className="relative z-10">{tab.label}</span>
                      {tab.badge && (
                        <span className="relative z-10 inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 text-[11px] font-semibold rounded-full"
                          style={{
                            backgroundColor: tab.badgeGreen ? 'var(--lime-tint)' : tab.badgeRed ? 'var(--lime-tint)' : 'rgba(32,33,31,0.07)',
                            color: tab.badgeGreen ? 'var(--brand-red)' : tab.badgeRed ? 'var(--brand-red)' : 'var(--ink)',
                          }}>
                          {tab.badge === '●' ? '●' : tab.badge}
                        </span>
                      )}
                    </button>
                  ))}
                </div>

                {/* Fixed-height animated content — flex-1 min-h-0 fills remaining space, overflow-y-auto scrolls */}
                <div className="flex-1 min-h-0 overflow-y-auto relative" role="tabpanel" id={`${tabId}-panel`} aria-labelledby={`${tabId}-${activeTab}-tab`} tabIndex={0}>
                  <motion.div
                    key={activeTab}
                    initial={reducedMotion ? false : { opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ duration: reducedMotion ? 0 : 0.12, ease: 'easeOut' }}
                    className="h-full"
                  >
                    {tabContent()}
                  </motion.div>
                </div>
              </>
            );
          })()}
        </div>
    </>
  );
};


// ── ContactDetailModal — stable Dialog shell ──────────────────────────────────
// Dialog + DialogContent stay mounted for the entire open → close lifecycle.
// Only ModalInner remounts when contactId changes, keeping the overlay stable.
export const ContactDetailModal = ({ contactId, defaultTab = 'overview', open, onClose, onDelete, returnFocusTo }) => (
  <Dialog open={open} onOpenChange={onClose}>
    <DialogContent
      hideClose
      onCloseAutoFocus={e => {
        const opener = returnFocusTo?.current;
        if (opener && opener.isConnected && typeof opener.focus === 'function') { e.preventDefault(); opener.focus(); }
      }}
      data-testid="contact-detail-modal"
      className="sp-contact-inspector p-0 border flex flex-col overflow-hidden"
      style={{
        backgroundColor: '#ffffff',
        borderColor: 'var(--stroke)',
        color: 'var(--text)',
        height: '100dvh',
        maxHeight: '100dvh',
        boxShadow: 'var(--shadow)',
      }}
    >
      {open && contactId ? (
        <ModalInner
          key={contactId}
          contactId={contactId}
          defaultTab={defaultTab}
          onClose={onClose}
          onDelete={onDelete}
        />
      ) : null}
    </DialogContent>
  </Dialog>
);
